import React, { Fragment } from 'react';
import { withMarketingData } from '@core-ui/marketing-provider';
import { withBreakpointData, XLARGE } from '@core-ui/breakpoint-provider';
import { withBrandData } from '@core-ui/brand-info-provider';
import {useTheme} from '@core-ui/react-stitch';
import usePmcsMarketing from '../../hooks/features/usePmcsMarketing';
import { propTypes } from './types';
import { FallbackHeader } from './fallback-header';
import { getColorName } from '../../../helpers/color'

const hasSVGOverlayData = marketingData => marketingData
  && marketingData.data
  && marketingData.data.background;
const marketingDataIncludes = (marketingData, imgName) => marketingData.data.background.content
  && marketingData.data.background.content[imgName];
const shouldShowBasicSVGBillboard = (greaterOrEqualTo, marketingData) => (greaterOrEqualTo(XLARGE)
  ? marketingDataIncludes(marketingData, 'largeImg')
  : marketingDataIncludes(marketingData, 'smallImg'));
const CATEGORY_BANNERS = 'category-banners';

const isPositionTop = position => position === 'top';

// Check if an object is empty
const isEmptyObject = obj => JSON.stringify(obj) === '{}'

const shouldShowMarketingHeader = (greaterOrEqualTo, marketingData) => (
  hasSVGOverlayData(marketingData)
    ? shouldShowBasicSVGBillboard(greaterOrEqualTo, marketingData)
    : marketingData && !isEmptyObject(marketingData)
);

const getDivisionMarketingData = (divisionMarketingData, did) => {
  return divisionMarketingData && divisionMarketingData[did];
};

const getCategoryBannersMarketingData = divisionMarketingData => {
  return divisionMarketingData && divisionMarketingData[CATEGORY_BANNERS];
};

const isCategoryNotExcluded = (divisionMarketingData, did, cid) => {
  divisionMarketingData = getDivisionMarketingData(divisionMarketingData, did);
  divisionMarketingData = getCategoryBannersMarketingData(divisionMarketingData);
  return divisionMarketingData && !divisionMarketingData?.excludedCids?.includes(cid);
}

const isDefinedInLegacyContract = (marketingData, position) => {
  return marketingData?.data?.components?[position] : false;
}
const isDefinedInPmcsContract = marketingData => Object.keys(marketingData || {}).some(key => key.includes('ebb'));

const shouldShowMarketingBanner = (marketingData, position) => {
  return isDefinedInLegacyContract(marketingData, position) || isDefinedInPmcsContract(marketingData);
};

const getDivisionID = selectedNodes => {
  const { id: did } = selectedNodes.find(({ type }) => type === 'division' || type === 'sub-division') || {};
  return did;
};

const getBannerName = (categoryName, subCategoryName) => {
  return subCategoryName ? `${subCategoryName} ${categoryName}` : categoryName;
}


const getCategoryID = selectedNodes => {
  const { id: cid } = selectedNodes.find(({ type }) => type === 'category') || {};
  return cid;
};

const getMarketingBannerComponentData = (marketingData, position) => {
  const components = marketingData?.data?.components;
  if (components) {
    return components[position];
  }
};

const getMarketingComponentData = (marketingData, position) => {
  let componentData;
  if (marketingData?.name === 'MarketingBanner') {
    componentData = getMarketingBannerComponentData(marketingData, position);
  } else if (position === 'top') {
    componentData = marketingData;
  }
  return componentData;
};

const getDivisionMarketingComponentData = (marketingComponentData, divisionMarketingData, did, cid, position) => {
  if (!marketingComponentData && isCategoryNotExcluded(divisionMarketingData, did, cid)) {
    return divisionMarketingData[did][CATEGORY_BANNERS].data.components[position];
  }
};

const getComponentData = (marketingData, divisionMarketingData, position, did, cid) => {
  const marketingComponentData = getMarketingComponentData(marketingData, position);
  return marketingComponentData || getDivisionMarketingComponentData(marketingComponentData, divisionMarketingData, did, cid, position);;
};

const getIsMarketingHeader = (greaterOrEqualTo, mktingData, position) => {
  return shouldShowMarketingHeader(greaterOrEqualTo, mktingData) && isPositionTop(position);
};

const shouldRenderMarketingHeader = (greaterOrEqualTo, mktingData, position, pmcsEnabled) => {
  const isMarketingHeader = getIsMarketingHeader(greaterOrEqualTo, mktingData, position);
  const isMarketingBanner = shouldShowMarketingBanner(mktingData, position);
  return isMarketingHeader || isMarketingBanner || pmcsEnabled;
};

const getMarketingData = (marketingData, divisionMarketingData, position, did, cid) => {
  const marketingComponentData = getMarketingComponentData(marketingData, position);
  if (!marketingComponentData && isCategoryNotExcluded(divisionMarketingData, did, cid)) {
    return divisionMarketingData[did][CATEGORY_BANNERS];
  }
  return marketingData;
};

export const getHeaderText = (colorName, categoryName) => { 
  return `${getColorName(colorName) || ""} ${categoryName}`.trim();
}

const getAccessibilityText = (marketingData, position, theme, categoryName, colorName) => {
  const isVisible = shouldShowMarketingBanner(marketingData, position) && isPositionTop(position);
  return isVisible
    ? <h1 css={theme.utilities.visuallyHidden}>{getHeaderText(colorName, categoryName)}</h1>
    : null;
};

export const shouldHideTitleText = (hasRailMobileFacets, breadcrumbs) => {
  const hasCategoryName = !!breadcrumbs?.category?.name;
  const hasHiddenTitle = hasRailMobileFacets && hasCategoryName;
  return hasHiddenTitle;
};

const marketingComponent = (
  PageMarketing,
  accessibilityText,
  marketingData,
  abbrBrand,
  componentData,
  criticalCss,
  fallbackHeader,
  position,
) => {
  return (
    <Fragment>
      {accessibilityText}
      <PageMarketing
        {...marketingData}
        brandName={abbrBrand}
        componentData={componentData}
        criticalCss={criticalCss}
        fallbackContent={fallbackHeader}
        mktingType={CATEGORY_BANNERS}
        position={position}
      />
    </Fragment>
  );
};

const CategoryBanner = ({
  categoryName,
  colorName,
  subcategoryName,
  criticalCss,
  hasHiddenTitle,
  position,
  selectedNodes = [],
  divisionMarketingData,
  brandData: {
    abbrBrand,
  },
  breakpointData: {
    greaterOrEqualTo,
    smallerThan,
  },
  marketingData: {
    pageSpecificMarketingData: marketingData,
    PageMarketing,
  },
}) => {
  const did = getDivisionID(selectedNodes);
  const bannerName =  getBannerName(categoryName, subcategoryName);
  const cid = getCategoryID(selectedNodes);
  const pmcsEnabled = usePmcsMarketing();
  const theme = useTheme();
  const componentData = getComponentData(marketingData, divisionMarketingData, position, did, cid);
  marketingData = getMarketingData(marketingData, divisionMarketingData, position, did, cid);
  const isRenderMarketingHeader = shouldRenderMarketingHeader(greaterOrEqualTo, marketingData, position, pmcsEnabled);
  const accessibilityText = getAccessibilityText(marketingData, position, theme, bannerName, colorName);
  const fallbackHeader = (
    <FallbackHeader
      position={position}
      shouldHideTitle={hasHiddenTitle && smallerThan(XLARGE)}
      bannerName={bannerName}
    />
  )

  if (isRenderMarketingHeader) {
    return marketingComponent(
      PageMarketing,
      accessibilityText,
      marketingData,
      abbrBrand,
      componentData,
      criticalCss,
      fallbackHeader,
      position,
    );
  }
  return fallbackHeader;
};

CategoryBanner.propTypes = propTypes;

export { CategoryBanner as RawCategoryBanner };

export default withBreakpointData(withBrandData(withMarketingData(CategoryBanner)));
